import { IServerResponse } from "@type/axios";

/**
 * Error thrown by the axios success interceptor when the backend returns an HTTP 2xx
 * response whose envelope reports failure (`isSuccess: false`). Before this existed, a
 * failure envelope resolved like a success, so `.then()` callbacks ran their success
 * side-effects (redirect, close drawer, refresh) on a failed request — see bug where
 * "Create New Build" navigated to the new build even when the copy failed.
 *
 * Extends Error so existing `catch (e) { message.error(getErrorMessage(e)) }` sites keep
 * working: `getErrorMessage` reads `.message`, which we seed from the envelope message.
 */
export class BusinessError extends Error {
  /** Business status code from the envelope (e.g. 400), distinct from the HTTP status (200). */
  readonly code?: string | number;
  /** The full response envelope, for callers that need more than the message. */
  readonly response: IServerResponse;
  /** Discriminator so callers can narrow without instanceof across bundle boundaries. */
  readonly isBusinessError = true;

  constructor(response: IServerResponse) {
    super(response?.message ?? "Request failed");
    this.name = "BusinessError";
    this.code = response?.code;
    this.response = response;
    // Restore the prototype chain: when TS compiles a class that extends Error to an older
    // target, `instanceof BusinessError` breaks without this reset.
    Object.setPrototypeOf(this, BusinessError.prototype);
  }
}

/**
 * Decide whether a resolved 2xx envelope represents success.
 *
 * IMPORTANT: only an explicit `isSuccess === false` is treated as failure. Responses with
 * no `isSuccess` field (blob downloads, the un-enveloped user-info body, router config) MUST
 * pass through as success — rejecting them would break every non-enveloped endpoint.
 */
export function isSuccessResponse(data: unknown): boolean {
  return !(typeof data === "object" && data !== null && (data as { isSuccess?: unknown }).isSuccess === false);
}
